import api from './api';
import { API_ENDPOINTS, USE_MOCK_DATA } from '../constants/config';
import { mockTeamLeads } from '../data/mockData';
import { getMockCompanies } from '../data/mockData';
import siteService from './siteService';
import { ROLES } from '../constants/roles';

class TeamLeadService {
  async getAllTeamLeads() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, data: mockTeamLeads });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.USERS_BY_ROLE(ROLES.TL));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching team leads:', error);
      return { success: false, error: error.message };
    }
  }

  async getTeamLeadById(tlId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const tl = mockTeamLeads.find(t => t.id === tlId);
          if (tl) {
            resolve({ success: true, data: tl });
          } else {
            resolve({ success: false, error: 'Team Lead not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.USER_DETAILS(tlId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching team lead:', error);
      return { success: false, error: error.message };
    }
  }

  async createTeamLead(tlData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const companyId = Number(tlData.company_id);
          const siteIds = Array.isArray(tlData.site_ids)
            ? tlData.site_ids.map(Number)
            : tlData.site_id
              ? [Number(tlData.site_id)]
              : [];
          const primarySiteId = siteIds[0] ?? null;

          const selectedCompany = getMockCompanies().find((company) => company.id === companyId);
          const sitesResult = await siteService.getAllSites();
          const selectedSites = sitesResult.success
            ? sitesResult.data.filter((site) => siteIds.includes(site.id))
            : [];
          const selectedSite = primarySiteId !== null
            ? selectedSites.find((site) => site.id === primarySiteId) || null
            : null;

          const newTL = {
            id: mockTeamLeads.length + 100,
            ...tlData,
            company_id: companyId,
            site_id: primarySiteId,
            site_ids: siteIds,
            company_name: selectedCompany?.company_name || 'Unassigned',
            company: { company_name: selectedCompany?.company_name || 'Unassigned' },
            sites: selectedSites,
            site_name: selectedSite?.name || selectedSite?.site_name || 'No Site',
            site: { name: selectedSite?.name || selectedSite?.site_name || 'No Site' },
            role: ROLES.TL,
            status: 'Available',
            engineers_count: 0,
            active_tickets: 0,
            completed_this_month: 0,
            password_last_updated: new Date().toISOString(),
            is_locked_out: false,
            mfa_enabled: false
          };
          
          mockTeamLeads.unshift(newTL);
          resolve({ success: true, data: newTL });
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.USERS, { ...tlData, role: ROLES.TL });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating team lead:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async updateTeamLead(tlId, tlData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const tlIndex = mockTeamLeads.findIndex(t => t.id === tlId);
          if (tlIndex !== -1) {
            const companyId = Number(tlData.company_id);
            const existingSiteIds = Array.isArray(mockTeamLeads[tlIndex].site_ids)
              ? mockTeamLeads[tlIndex].site_ids
              : mockTeamLeads[tlIndex].site_id
                ? [mockTeamLeads[tlIndex].site_id]
                : [];
            const siteIds = Array.isArray(tlData.site_ids)
              ? tlData.site_ids.map(Number)
              : tlData.site_id
                ? [Number(tlData.site_id)]
                : existingSiteIds;
            const primarySiteId = siteIds[0] ?? null;

            const selectedCompany = getMockCompanies().find((company) => company.id === companyId);
            const sitesResult = await siteService.getAllSites();
            const selectedSites = sitesResult.success
              ? sitesResult.data.filter((site) => siteIds.includes(site.id))
              : mockTeamLeads[tlIndex].sites || [];
            const selectedSite = primarySiteId !== null
              ? selectedSites.find((site) => site.id === primarySiteId) || null
              : null;

            mockTeamLeads[tlIndex] = {
              ...mockTeamLeads[tlIndex],
              ...tlData,
              company_id: Number.isNaN(companyId) ? mockTeamLeads[tlIndex].company_id : companyId,
              site_id: primarySiteId,
              site_ids: siteIds,
              company_name: selectedCompany?.company_name || mockTeamLeads[tlIndex].company_name || 'Unassigned',
              company: { company_name: selectedCompany?.company_name || mockTeamLeads[tlIndex].company_name || 'Unassigned' },
              sites: selectedSites,
              site_name: selectedSite?.name || selectedSite?.site_name || mockTeamLeads[tlIndex].site_name || 'No Site',
              site: { name: selectedSite?.name || selectedSite?.site_name || mockTeamLeads[tlIndex].site_name || 'No Site' }
            };
            resolve({ success: true, data: mockTeamLeads[tlIndex] });
          } else {
            resolve({ success: false, error: 'Team Lead not found' });
          }
        }, 500);
      });
    }

    try {
      const response = await api.put(API_ENDPOINTS.USER_DETAILS(tlId), tlData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating team lead:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async deleteTeamLead(tlId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const tlIndex = mockTeamLeads.findIndex(t => t.id === tlId);
          if (tlIndex !== -1) {
            const tl = mockTeamLeads[tlIndex];
            
            if (tl.engineers_count > 0 || tl.active_tickets > 0) {
              resolve({ 
                success: false, 
                error: `Cannot delete Team Lead with assigned engineers or active team tickets.` 
              });
            } else {
              mockTeamLeads.splice(tlIndex, 1);
              resolve({ success: true });
            }
          } else {
            resolve({ success: false, error: 'Team Lead not found' });
          }
        }, 500);
      });
    }

    try {
      await api.delete(API_ENDPOINTS.USER_DETAILS(tlId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting team lead:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getTeamLeadPerformance(tlId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const tl = mockTeamLeads.find(t => t.id === tlId);
          if (tl) {
            resolve({ 
              success: true, 
              data: {
                team_lead_id: tlId,
                team_performance_score: 88,
                completed_this_month_team: tl.completed_this_month,
                active_tickets_team: tl.active_tickets,
                engineers_managed: tl.engineers_count
              }
            });
          } else {
            resolve({ success: false, error: 'Team Lead not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.TL_PERFORMANCE(tlId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching team lead performance:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new TeamLeadService();
